import React, { ComponentType, useEffect } from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';

import { useStores } from 'src/hooks/use-stores';
import { useObserver } from 'mobx-react-lite';
import useLocalStorage from 'react-use-localstorage';

type PrivateRouteProps = Omit<RouteProps, 'children' | 'component' | 'render'> & {
  component: ComponentType<RouteProps>;
};

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function PrivateRoute({
  // JSX component constructors must be PascalCase.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  component: Component,
  ...rest
}: PrivateRouteProps): JSX.Element | null {
  const { User } = useStores();
  const location = useLocation();
  const { pathname: locationPathname, search: locationSearch } = location;
  const [, setRedirectAfterLogin] = useLocalStorage('redirectAfterLogin', '');
  const initialUserFetchFinished = useObserver(() => User.initialUserFetchFinished);
  const loggedIn = useObserver(() => User.loggedIn);

  useEffect(() => {
    if (!loggedIn) {
      setRedirectAfterLogin(locationPathname + locationSearch);
    }
  }, [locationPathname, locationSearch, loggedIn, setRedirectAfterLogin]);

  if (!initialUserFetchFinished) {
    return null;
  }

  return (
    <Route {...rest}>
      {loggedIn && <Component {...rest} />}
      {!loggedIn && <Redirect to={{ pathname: '/login', state: { from: location } }} />}
    </Route>
  );
}

export default PrivateRoute;
